/**
 * Landing page CashLive — alignée sur le design system du dashboard
 */

import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Building2,
  CreditCard,
  BarChart3,
  Shield,
  ArrowRight,
  CheckCircle,
  Truck,
  ChevronDown,
  Mail,
  Phone,
  MapPin,
  Send,
  LayoutDashboard,
  Menu,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/providers";
import AuthModal from "@/components/auth/AuthModal";
import { LanguageSwitcher } from "@/shared/ui";
import SeoTags from "@/components/shared/SeoTags";
import VerticalFeaturesSection from "@/components/landing/VerticalFeaturesSection";
import FeesSavingsSimulator from "@/components/landing/FeesSavingsSimulator";
import { CONTACT_INFO } from "@/shared/constants/contact";
import { Button, Card, CardBody } from "@/components/ui";
import dashboardPreview from "@/assets/features/tableau-de-bord-des-commandes-Cashlive.png";
import "@/pages/landing-page-optimizations.css";

const HOW_IT_WORKS_STEPS = [
  { step: "1", icon: Building2 },
  { step: "2", icon: CreditCard },
  { step: "3", icon: BarChart3 },
] as const;

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  let currentUser = null;
  try {
    const auth = useAuth();
    currentUser = auth.currentUser;
  } catch {
    // Auth non disponible hors provider
  }

  const { affiliations } = useAuth();
  const hasOrganizations = affiliations && affiliations.length > 0;

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const [quoteName, setQuoteName] = useState("");
  const [quoteEmail, setQuoteEmail] = useState("");
  const [quoteService, setQuoteService] = useState("");
  const [quoteProject, setQuoteProject] = useState("");

  useEffect(() => {
    if (location.pathname === "/auth/login" || location.pathname === "/auth/register") {
      setShowAuthModal(true);
      setIsRegisterMode(location.pathname === "/auth/register");
    }
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const closeAuthModal = useCallback(() => {
    setShowAuthModal(false);
    setIsRegisterMode(false);
    if (location.pathname === "/auth/login" || location.pathname === "/auth/register") {
      globalThis.history.replaceState({}, "", "/");
    }
  }, [location.pathname]);

  const openAuthModal = useCallback((registerMode: boolean) => {
    setShowAuthModal(true);
    setIsRegisterMode(registerMode);
    setMobileMenuOpen(false);
  }, []);

  const scrollTo = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  }, []);

  const handleQuoteSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const subject = `Demande de devis - ${quoteService || "CashLive"}`;
      const body = `
${t("landing.contactEmail.greeting")}

${t("landing.contactEmail.intro")}

${t("landing.contactEmail.nameLabel")} ${quoteName}
${t("landing.contactEmail.emailLabel")} ${quoteEmail}
${t("landing.contactEmail.serviceLabel")} ${quoteService}

${t("landing.contactEmail.projectLabel")}
${quoteProject}

${t("landing.contactEmail.closing")}

${t("landing.contactEmail.signature")}
${quoteName}
    `.trim();

      globalThis.location.href = `mailto:${CONTACT_INFO.EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      setTimeout(() => {
        setQuoteName("");
        setQuoteEmail("");
        setQuoteService("");
        setQuoteProject("");
      }, 500);
    },
    [quoteName, quoteEmail, quoteService, quoteProject, t],
  );

  const primaryCtaHref = hasOrganizations ? "/organizations/manage" : "/organizations/create";
  const primaryCtaLabel = hasOrganizations
    ? t("header.myOrganizations")
    : t("landing.hero.cta");

  const structuredData = {
    "@context": "https://schema.org",
    "@type": ["Organization", "WebApplication"],
    name: "CashLive",
    url: "https://cashlive.fr",
    logo: "https://cashlive.fr/assets/logo.jpg",
    description: t("landing.structuredData.description"),
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
      description: t("landing.structuredData.offerDescription"),
    },
    sameAs: ["https://www.instagram.com/cashlive", "https://www.tiktok.com/@cashlive"],
  };

  const heroFeatures = t("landing.hero.features", { returnObjects: true }) as string[];
  const beforePoints = t("landing.comparison.before.points", { returnObjects: true }) as string[];
  const afterPoints = t("landing.comparison.after.points", { returnObjects: true }) as string[];
  const faqItems = t("landing.faq.questions", {
    returnObjects: true,
  }) as Array<{ question: string; answer: string }>;
  const valueFeatures = t("landing.valueProp.features", { returnObjects: true }) as string[];

  return (
    <div className="min-h-screen bg-background text-foreground landing-page">
      <SeoTags
        title={t("landing.seo.title")}
        description={t("landing.seo.description")}
        url="https://cashlive.fr"
        structuredData={structuredData}
      />

      {/* Header — fixed pour rester visible au scroll */}
      <header className="fixed top-0 left-0 right-0 z-50">
        <div
          className={`bg-background-secondary/95 backdrop-blur-md transition-shadow duration-300 ${
            scrolled || mobileMenuOpen
              ? "border-b border-border shadow-sm"
              : "border-b border-border"
          }`}
        >
          <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
            <Link to="/" className="group flex items-center gap-2.5 shrink-0">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary shadow-sm transition-transform duration-200 group-hover:scale-105">
                <Building2 className="text-white" style={{ width: 18, height: 18 }} />
              </div>
              <span className="text-base sm:text-lg font-bold tracking-tight text-foreground">
                CashLive
              </span>
            </Link>

            <nav className="hidden lg:flex items-center rounded-full bg-background p-1 ring-1 ring-border">
              {[
                { id: "how-it-works", label: t("landing.howItWorks.title") },
                { id: "key-features", label: t("landing.features.title") },
                { id: "faq", label: "FAQ" },
                { id: "contact", label: "Contact" },
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollTo(item.id)}
                  className="rounded-full px-3.5 py-1.5 text-sm font-medium text-foreground-secondary transition-colors hover:bg-background-tertiary hover:text-foreground"
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="hidden sm:flex items-center gap-2">
              <LanguageSwitcher variant="dark" showName={false} />
              {currentUser ? (
                <Link to="/my-orders">
                  <Button size="sm" variant="primary" className="!rounded-full !px-4">
                    Mon compte
                  </Button>
                </Link>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="!rounded-full !px-3 !text-slate-300 hover:!text-white hover:!bg-slate-800"
                    onClick={() => openAuthModal(false)}
                  >
                    {t("header.signIn")}
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    className="!rounded-full !px-4"
                    onClick={() => openAuthModal(true)}
                    rightIcon={<ArrowRight className="h-3.5 w-3.5" />}
                  >
                    Commencer
                  </Button>
                </>
              )}
            </div>

            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors hover:bg-background-tertiary lg:hidden"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 top-16 z-40 bg-foreground/10 backdrop-blur-[2px] lg:hidden"
              aria-label="Fermer le menu"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="absolute inset-x-0 top-full z-50 border-b border-border bg-background-secondary shadow-lg lg:hidden">
              <div className="mx-auto max-w-6xl px-4 sm:px-6 py-4">
                <nav className="flex flex-col gap-1">
                  {[
                    { id: "how-it-works", label: t("landing.howItWorks.title") },
                    { id: "key-features", label: t("landing.features.title") },
                    { id: "faq", label: "FAQ" },
                    { id: "contact", label: "Contact" },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => scrollTo(item.id)}
                      className="rounded-xl px-3 py-3 text-left text-sm font-medium text-foreground transition-colors hover:bg-background"
                    >
                      {item.label}
                    </button>
                  ))}
                </nav>

                <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:hidden">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide text-foreground-secondary">
                      Langue
                    </span>
                    <LanguageSwitcher variant="dark" showName={false} />
                  </div>
                  {currentUser ? (
                    <Link to="/my-orders" onClick={() => setMobileMenuOpen(false)}>
                      <Button size="md" variant="primary" className="w-full !rounded-full">
                        Mon compte
                      </Button>
                    </Link>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="md"
                        variant="outline"
                        className="!rounded-full"
                        onClick={() => openAuthModal(false)}
                      >
                        {t("header.signIn")}
                      </Button>
                      <Button
                        size="md"
                        variant="primary"
                        className="!rounded-full"
                        onClick={() => openAuthModal(true)}
                      >
                        Commencer
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </header>

      {/* Espace sous la navbar fixed */}
      <div className="h-16" aria-hidden="true" />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-background-secondary">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_color-mix(in_srgb,var(--primary)_8%,transparent)_0%,_transparent_55%)]" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-0">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-3 text-sm font-medium text-primary">{t("landing.hero.badge")}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
              CashLive
            </h1>
            <p className="mt-3 text-xl sm:text-2xl font-semibold text-foreground">
              {t("landing.hero.subtitle")}
            </p>
            <p className="mt-4 text-base sm:text-lg text-foreground-secondary leading-relaxed max-w-2xl mx-auto">
              {t("landing.hero.tagline")}
            </p>

            <ul className="mt-6 flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-3 sm:gap-x-6">
              {heroFeatures.map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm text-foreground-secondary">
                  <CheckCircle className="h-4 w-4 shrink-0 text-success" />
                  <span>{feature.replace(/^[^\wÀ-ÿ]+/, "")}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              {currentUser ? (
                <Link to={primaryCtaHref} className="w-full sm:w-auto">
                  <Button size="lg" variant="primary" className="w-full sm:w-auto" rightIcon={<ArrowRight className="h-4 w-4" />}>
                    {primaryCtaLabel}
                  </Button>
                </Link>
              ) : (
                <Button
                  size="lg"
                  variant="primary"
                  className="w-full sm:w-auto"
                  onClick={() => openAuthModal(false)}
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                >
                  {t("landing.hero.cta")}
                </Button>
              )}
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => scrollTo("how-it-works")}
              >
                {t("landing.hero.ctaSecondary")}
              </Button>
            </div>
          </div>

          <div className="mt-10 sm:mt-14 mx-auto max-w-5xl">
            <div className="overflow-hidden rounded-t-xl border border-b-0 border-border bg-surface shadow-lg">
              <div className="flex items-center gap-1.5 border-b border-border bg-background-tertiary px-3 py-2">
                <span className="h-2.5 w-2.5 rounded-full bg-border-dark" />
                <span className="h-2.5 w-2.5 rounded-full bg-border-dark" />
                <span className="h-2.5 w-2.5 rounded-full bg-border-dark" />
              </div>
              <img
                src={dashboardPreview}
                alt="Tableau de bord CashLive"
                className="w-full h-auto block"
                loading="eager"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="py-14 sm:py-20 bg-background">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              {t("landing.valueProp.title")}
            </h2>
            <p className="mt-3 text-foreground-secondary leading-relaxed">
              {t("landing.valueProp.description")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: CreditCard, label: "Paiements sécurisés" },
              { icon: Truck, label: "Expédition automatisée" },
              { icon: LayoutDashboard, label: "Suivi des commandes" },
              { icon: Shield, label: "Sans abonnement" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.label} variant="outlined" className="!rounded-lg">
                  <CardBody className="flex items-center gap-3 sm:flex-col sm:items-start sm:gap-4 p-4 sm:p-5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-foreground">{item.label}</span>
                  </CardBody>
                </Card>
              );
            })}
          </div>

          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
            {valueFeatures.map((feature) => (
              <li key={feature} className="flex items-start gap-2 text-sm text-foreground-secondary">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-14 sm:py-20 bg-background-secondary border-y border-border">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              {t("landing.feesComparison.title")}
            </h2>
            <p className="mt-2 text-foreground-secondary">
              {t("landing.feesComparison.subtitle")}
            </p>
          </div>

          <div className="mb-6 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-stretch gap-3 sm:gap-4 max-w-3xl mx-auto">
            <div className="rounded-xl border border-error/30 bg-error/5 px-5 py-5 text-center">
              <p className="text-xs font-semibold uppercase tracking-wide text-error/80">
                {t("landing.feesComparison.tiktokLabel")}
              </p>
              <p className="mt-2 text-3xl sm:text-4xl font-bold text-error tabular-nums">
                {t("landing.feesComparison.tiktokRate")}
              </p>
              <p className="mt-1 text-sm text-foreground-secondary">
                {t("landing.feesComparison.tiktokHint")}
              </p>
            </div>

            <div className="hidden sm:flex items-center justify-center px-1">
              <span className="text-sm font-semibold text-foreground-secondary">vs</span>
            </div>

            <div className="rounded-xl border border-success/40 bg-success/10 px-5 py-5 text-center">
              <p className="text-xs font-semibold uppercase tracking-wide text-success-dark">
                {t("landing.feesComparison.cashliveLabel")}
              </p>
              <p className="mt-2 text-3xl sm:text-4xl font-bold text-success tabular-nums">
                {t("landing.feesComparison.cashliveRate")}
              </p>
              <p className="mt-1 text-sm text-foreground-secondary">
                {t("landing.feesComparison.cashliveHint")}
              </p>
            </div>
          </div>

          <div className="mb-8 max-w-3xl mx-auto">
            <FeesSavingsSimulator />
          </div>

          <p className="mx-auto mb-10 max-w-2xl text-center text-sm text-foreground-secondary leading-relaxed">
            {t("landing.feesComparison.note")}
          </p>

          <h3 className="text-center text-xl sm:text-2xl font-bold text-foreground mb-8">
            {t("landing.comparison.title", { defaultValue: "La différence est claire" })}
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card variant="outlined">
              <CardBody className="p-5 sm:p-6">
                <h3 className="text-lg font-semibold text-error mb-1">
                  {t("landing.comparison.before.title")}
                </h3>
                <p className="text-sm text-foreground-secondary mb-5">
                  {t("landing.comparison.before.subtitle")}
                </p>
                <ul className="space-y-2.5">
                  {beforePoints.slice(0, 8).map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm text-foreground-secondary">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-error/50" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>

            <Card variant="elevated" className="border-primary-20 !border">
              <CardBody className="p-5 sm:p-6">
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  {t("landing.comparison.after.title")}
                </h3>
                <p className="text-sm text-primary mb-5">
                  {t("landing.comparison.after.subtitle")}
                </p>
                <ul className="space-y-3">
                  {afterPoints.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm text-foreground">
                      <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-border">
                  <p className="text-sm font-medium text-foreground mb-4">
                    {t("landing.comparison.after.description")}
                  </p>
                  {currentUser ? (
                    <Link to={primaryCtaHref}>
                      <Button variant="primary" className="w-full" rightIcon={<ArrowRight className="h-4 w-4" />}>
                        {t("landing.hero.cta")}
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={() => openAuthModal(false)}
                      rightIcon={<ArrowRight className="h-4 w-4" />}
                    >
                      {t("landing.hero.cta")}
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-14 sm:py-20 bg-background">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-10 sm:mb-14">
            <p className="text-sm font-medium text-primary mb-2">{t("landing.howItWorks.badge")}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              {t("landing.howItWorks.title")}
              {t("landing.howItWorks.titleHighlight")}
            </h2>
            <p className="mt-3 text-foreground-secondary">{t("landing.howItWorks.subtitle")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {HOW_IT_WORKS_STEPS.map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.step} variant="default">
                  <CardBody className="p-5 sm:p-6 text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-white">
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-md bg-background-tertiary px-2 text-xs font-semibold text-foreground-secondary mb-3">
                      {item.step}
                    </span>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {t(`landing.howItWorks.steps.${item.step}.title`)}
                    </h3>
                    <p className="text-sm text-foreground-secondary leading-relaxed">
                      {t(`landing.howItWorks.steps.${item.step}.description`)}
                    </p>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <VerticalFeaturesSection
        currentUser={currentUser}
        hasOrganizations={hasOrganizations}
        openAuthModal={openAuthModal}
        t={t}
      />

      {/* FAQ */}
      <section id="faq" className="py-14 sm:py-20 bg-background border-t border-border">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-foreground mb-8 sm:mb-10">
            {t("landing.faq.title")}{" "}
            <span className="text-primary">{t("landing.faq.titleHighlight")}</span>
          </h2>

          <div className="space-y-2">
            {faqItems.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <div key={faq.question} className="rounded-lg border border-border bg-surface overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-background-secondary transition-colors"
                  >
                    <span className="text-sm sm:text-base font-medium text-foreground">{faq.question}</span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-foreground-secondary transition-transform ${isOpen ? "rotate-180 text-primary" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="border-t border-border px-4 py-3 bg-background-secondary">
                      <p className="text-sm text-foreground-secondary leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-14 sm:py-20 bg-background-secondary border-t border-border">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              {t("landing.contact.title")}
              <span className="text-primary">{t("landing.contact.titleHighlight")}</span>
            </h2>
            <p className="mt-3 text-foreground-secondary">{t("landing.contact.subtitle")}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card variant="default">
              <CardBody className="p-5 sm:p-6 space-y-5">
                <h3 className="text-lg font-semibold text-foreground">
                  {t("landing.contact.info.title")}
                </h3>
                {[
                  { icon: Mail, label: t("landing.contact.info.email"), value: CONTACT_INFO.EMAIL },
                  { icon: Phone, label: t("landing.contact.info.phone"), value: CONTACT_INFO.PHONE_DISPLAY },
                  { icon: MapPin, label: t("landing.contact.info.address"), value: "Paris, France" },
                ].map((row) => {
                  const Icon = row.icon;
                  return (
                    <div key={row.label} className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-10">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{row.label}</p>
                        <p className="text-sm text-foreground-secondary truncate">{row.value}</p>
                      </div>
                    </div>
                  );
                })}
              </CardBody>
            </Card>

            <Card variant="default">
              <CardBody className="p-5 sm:p-6">
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  {t("landing.contact.form.title")}
                </h3>
                <p className="text-sm text-foreground-secondary mb-5">
                  {t("landing.contact.form.subtitle")}
                </p>
                <form onSubmit={handleQuoteSubmit} className="space-y-3">
                  <input
                    type="text"
                    placeholder={t("landing.contact.form.name")}
                    value={quoteName}
                    onChange={(e) => setQuoteName(e.target.value)}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm text-foreground placeholder:text-foreground-secondary focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary"
                    required
                  />
                  <input
                    type="email"
                    placeholder={t("landing.contact.form.email")}
                    value={quoteEmail}
                    onChange={(e) => setQuoteEmail(e.target.value)}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm text-foreground placeholder:text-foreground-secondary focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary"
                    required
                  />
                  <input
                    type="text"
                    placeholder={t("landing.contact.form.service")}
                    value={quoteService}
                    onChange={(e) => setQuoteService(e.target.value)}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm text-foreground placeholder:text-foreground-secondary focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary"
                    required
                  />
                  <textarea
                    placeholder={t("landing.contact.form.project")}
                    rows={4}
                    value={quoteProject}
                    onChange={(e) => setQuoteProject(e.target.value)}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-sm text-foreground placeholder:text-foreground-secondary focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary resize-none"
                    required
                  />
                  <Button type="submit" variant="primary" className="w-full" leftIcon={<Send className="h-4 w-4" />}>
                    {t("landing.contact.form.submit")}
                  </Button>
                </form>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 sm:py-16 bg-primary">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            {t("landing.finalCta.title")}
          </h2>
          <p className="mt-3 text-sm sm:text-base text-white/80 leading-relaxed">
            {t("landing.finalCta.subtitle")}
          </p>
          <div className="mt-8">
            {currentUser ? (
              <Link to={primaryCtaHref}>
                <Button
                  size="lg"
                  className="!bg-surface !text-primary hover:!bg-background-secondary"
                  rightIcon={<ArrowRight className="h-4 w-4" />}
                >
                  {primaryCtaLabel}
                </Button>
              </Link>
            ) : (
              <Button
                size="lg"
                className="!bg-surface !text-primary hover:!bg-background-secondary"
                onClick={() => openAuthModal(true)}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                {t("landing.freePlan.cta")}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-surface py-10 sm:py-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-8">
            <div className="max-w-md">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary">
                  <Building2 className="h-4 w-4 text-white" />
                </div>
                <span className="font-bold text-foreground">CashLive</span>
              </div>
              <p className="text-sm text-foreground-secondary leading-relaxed">
                {t("landing.footer.tagline")}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">
                {t("landing.footer.legalTitle")}
              </h3>
              <ul className="space-y-2 text-sm text-foreground-secondary">
                <li>
                  <a href="/cgu.html" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                    {t("landing.footer.terms")}
                  </a>
                </li>
                <li>
                  <a href="/politique-confidentialite.html" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                    {t("landing.footer.privacy")}
                  </a>
                </li>
                <li>
                  <a href="/mentions-legales.html" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                    {t("landing.footer.legal")}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 border-t border-border pt-6 text-sm text-foreground-secondary">
            © {new Date().getFullYear()} CashLive. {t("landing.footer.rights")}.
          </div>
        </div>
      </footer>

      <AuthModal
        isOpen={showAuthModal}
        isRegisterMode={isRegisterMode}
        onClose={closeAuthModal}
        onModeChange={setIsRegisterMode}
      />
    </div>
  );
};

export default LandingPage;
